import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Shield, Plus, Trash2, ChevronRight, Users } from "lucide-react";

import { useConfirm } from "@/context/ConfirmContext";
export default function Roles() {
  const { user } = useAuth();
  const confirmar = useConfirm();
  const navigate = useNavigate();
  const [roles, setRoles] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ nombre: "", descripcion: "" });
  const [busy, setBusy] = useState(false);

  const cargar = () => api.get("/permisos").then((r) => setRoles(r.data.roles));
  useEffect(() => { cargar(); }, []);

  if (user && !user.es_admin) return <Navigate to="/" replace />;

  const crear = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/roles", form);
      const nuevo = data.roles.find((r) => r.nombre === form.nombre.trim());
      toast.success("Rol creado");
      setOpen(false); setForm({ nombre: "", descripcion: "" });
      if (nuevo) navigate(`/roles/${nuevo.id}`);
      else cargar();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const eliminar = async (e, rol) => {
    e.stopPropagation();
    if (!(await confirmar({ titulo: `¿Eliminar el rol "${rol.nombre}"?`, accion: "Eliminar rol", peligro: true }))) return;
    try {
      const { data } = await api.delete(`/roles/${rol.id}`);
      setRoles(data.roles);
      toast.success("Rol eliminado");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="p-6 lg:p-8 max-w-4xl">
      <div className="flex items-start justify-between mb-8 flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-xl bg-slate-900 text-white flex items-center justify-center"><Shield size={18} /></div>
            <h1 className="font-head text-3xl font-extrabold tracking-tight">Roles</h1>
          </div>
          <p className="text-slate-500 mt-1">Crea roles con permisos definidos y asígnalos a los usuarios.</p>
        </div>
        <Button className="rounded-lg" onClick={() => { setForm({ nombre: "", descripcion: "" }); setOpen(true); }} data-testid="new-role-button">
          <Plus size={16} className="mr-1.5" /> Crear rol
        </Button>
      </div>

      {!roles ? (
        <Skeleton className="h-72 rounded-xl" />
      ) : (
        <Card className="border-slate-200 shadow-sm divide-y divide-slate-100 overflow-hidden">
          {roles.map((r) => (
            <button
              key={r.id}
              onClick={() => navigate(`/roles/${r.id}`)}
              className="w-full flex items-center gap-4 px-5 py-4 hover:bg-slate-50 transition-colors text-left"
              data-testid={`role-item-${r.id}`}
            >
              <span className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ${r.color || "bg-slate-100 text-slate-700"}`}>
                <Users size={18} />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-slate-800">{r.nombre}</span>
                  {r.sistema && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500">Sistema</span>}
                </div>
                <p className="text-sm text-slate-500 truncate">{r.descripcion || "Sin descripción."}</p>
              </div>
              {!r.sistema && (
                <button onClick={(e) => eliminar(e, r)} className="text-slate-400 hover:text-red-600 p-1.5 transition-colors shrink-0" data-testid={`delete-role-${r.id}`}>
                  <Trash2 size={16} />
                </button>
              )}
              <ChevronRight size={18} className="text-slate-300 shrink-0" />
            </button>
          ))}
        </Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Crear rol</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Nombre</Label>
              <Input value={form.nombre} maxLength={255} onChange={(e) => setForm({ ...form, nombre: e.target.value })} placeholder="Ej. Customer Care" data-testid="new-role-nombre" />
            </div>
            <div className="space-y-1.5">
              <Label>Descripción</Label>
              <Textarea rows={3} value={form.descripcion} maxLength={255} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} placeholder="¿Qué puede hacer este rol?" data-testid="new-role-descripcion" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={crear} disabled={busy || !form.nombre.trim()} data-testid="new-role-save">Crear y configurar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
